import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Grid, Box, Button } from '@material-ui/core';
import { ArrowForward, CreateNewFolder, Delete, Edit } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';

import { WebContext } from 'src/scenes/web-context';
import { TitleContainer, ContentContainer, Cards, ActionsBox, DialogWrap, AccessControl } from 'src/components';
import { useProjectApi } from 'src/services';
import { ProjectCard } from 'src/scenes/Projects/components/ProjectCard';
import { ProjectCreateDialog, ProjectDeleteDialog, ProjectEditDialog } from 'src/scenes/CompanySummary/components';
import { Pagination, PaginationItem } from '@material-ui/lab';
import { useFetchStatus, useFilters } from 'src/hooks';


const useStyles = makeStyles(theme => ({
  mainContainer: {
    width: theme.spacing(180),
  },
  defaultItem: {
    padding: theme.spacing(1),
  },
  contentBox: {
    width: 1000,
    [theme.breakpoints.up('md')]: {
      width: 880,
    },
    [theme.breakpoints.up('lg')]: {
      width: 1000,
    },
  },
}));

const ProjectsContainer = () => {

  const classes = useStyles();
  const history = useHistory();
  const { search } = useLocation();
  const { fetchStatus: fetchProjectStatus, setFetchStatus, fetchStatusEnum } = useFetchStatus();


  const projectApi = useProjectApi();
  const [ projects, setProjects ] = useState([]);
  const [ projectsCount, setProjectsCount ] = useState(0);

  const { currentCompany: { companyName } } = useContext(WebContext);

  const getQueryParams = () => {
    const query = new URLSearchParams(location.search);
    const fixedSize = 10;
    query.set('size', fixedSize);
    const page = parseInt(query.get('page') || 1, 10);
    return { page, size: fixedSize };
  };

  const { setFilter, query } = useFilters({ history, location });
  const { page, size } = getQueryParams();
  const totalPages = Math.ceil(projectsCount / size);

  const fetchProjects = async () => {
    const { totalProjectsCount, projectList } = await projectApi.getProjects(query.toString());
    setProjects(projectList);
    setProjectsCount(totalProjectsCount);
  };

  useEffect(() => {
    setFetchStatus(fetchStatusEnum.LOADING);
    fetchProjects();
    setFetchStatus(fetchStatusEnum.SUCCESS);
  }, [ search ]); // eslint-disable-line

  useEffect(() => {
    document.title = companyName ? `${companyName} - Proyectos` : 'Nviro Monitor';
  }, [ companyName ]);

  useEffect(() => {
    if (projects.length === 0 && page > 1 && fetchProjectStatus === fetchStatusEnum.SUCCESS) {
      setFilter({ id: 'page', value: page - 1 });
    }
  }, [ projects.length ]); // eslint-disable-line

  const [ dialog, setDialog ] = useState({ isOpen: false, type: '' });
  const closeDialog = () => setDialog({ isOpen: false, type: '' });
  const openDialog = (type, data = {}) => setDialog({ isOpen: true, type, data });

  const openCreateProjectDialog = () => openDialog('create-project');
  const openDeleteProjectDialog = data => openDialog('delete-project', data);
  const openEditProjectDialog = data => openDialog('edit-project', data);

  const getDialogType = () => {
    switch (dialog.type) {
      case 'create-project':
        return <ProjectCreateDialog actions={{ closeDialog, createProject }} />;
      case 'delete-project':
        return <ProjectDeleteDialog project={dialog.data} actions={{ closeDialog, deleteProject }} />;
      case 'edit-project':
        return <ProjectEditDialog project={dialog.data} actions={{ closeDialog, editProject }} />;
      default:
        break;
    }
  };

  const goToProject = project => history.push(`/web/projects/${project.id}`);

  const cardActions = useCallback(itemData => [
    {
      Icon: Edit,
      tooltip: 'Editar',
      onClick: () => openEditProjectDialog(itemData),
      accessPermission: 'project:edit',
    },
    {
      Icon: Delete,
      tooltip: 'Eliminar',
      onClick: () => openDeleteProjectDialog(itemData),
      accessPermission: 'project:delete',
    },
    {
      Icon: ArrowForward,
      tooltip: 'Ver proyecto',
      onClick: () => goToProject(itemData),
    },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderProjectCard = useCallback(({ itemData }) => (
    <ProjectCard {...itemData} />
  ), []);

  const createProject = async newProject => {
    const [ createdProject ] = await projectApi.createProject(newProject);
    setProjects(prevProjects => [
      createdProject,
      ...(prevProjects?.length === size ? prevProjects.slice(0, -1) : prevProjects),
    ]);
    setProjectsCount(projectsCount => projectsCount + 1);
    if (page > 1) {
      setFilter({ id: 'page', value: 1 });
    }
  };

  const deleteProject = async projectId => {
    await projectApi.deleteProject(projectId);
    fetchProjects();
  };

  const editProject = async editProject => {
    await projectApi.updateProject({ projectId: editProject.id, name: editProject.name });
    setProjects(projects.map(project => editProject.id === project.id ? editProject : project));
  };

  return (
    <Grid container justifyContent="center" className={classes.mainContainer}>
      <TitleContainer>
        Proyectos de {companyName}
      </TitleContainer>
      <ContentContainer>
        <AccessControl action='project:create'>
          <ActionsBox>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={openCreateProjectDialog}
              startIcon={<CreateNewFolder fontSize="small" />}
            >
              Crear proyecto
            </Button>
          </ActionsBox>
        </AccessControl>
        <Box display="flex" alignItems="center" flexDirection="column">
          <Box py={4} className={classes.contentBox}>
            <Cards
              classes={{ defaultItem: classes.defaultItem }}
              values={projects}
              actions={cardActions}
              Content={renderProjectCard}
            />
            {projectsCount > size &&
              <Box pt={3} display="flex" justifyContent="center">
                <Pagination
                  page={page}
                  count={totalPages}
                  variant="outlined"
                  shape="rounded"
                  color="primary"
                  onChange={(_, page) => {
                    setFilter({ id: 'size', value: size });
                    setFilter({ id: 'page', value: page });
                  }}
                  renderItem={item => (
                    <PaginationItem {...item} />
                  )}
                />
              </Box>
            }
          </Box>
        </Box>
      </ContentContainer>
      <DialogWrap maxWidth='sm' fullWidth onClose={closeDialog} aria-labelledby='form-dialog-title' open={dialog.isOpen}>
        {getDialogType()}
      </DialogWrap>
    </Grid>
  );
};


export {
  ProjectsContainer,
};
